// Pure data layer of "Copiar configuración de otro local". The back returns the sanitized config of a
// local of a related negocio (GET /organizer/locales/:id/config-importable); this file turns it into
// the state of each guided first run (club, bar, salón) so the organizer reviews it before saving,
// and holds the pure pieces of the steps that run after the local is created (birthdays of a club).
// Images are never copied: the back returns URLs for preview only and the organizer uploads again.
import { DIAS_SEMANA } from "../reservas/primerUso/barPayload";
import { nuevoServicio, separarTelefono } from "../turnos/primerUso/salonPayload";
import { buildBirthdaysPayload, hasBirthdayErrors, normalizeBirthdays, validateBirthdays } from "../canchas/clubBirthdays";

export const TIPOS_IMPORTABLES = ["club", "bar", "beauty"];

const AVISOS_AJUSTES = {
	deposito_removido:
		"Tu negocio todavía no puede cobrar señas, así que copiamos la configuración como reservas sin seña.",
	whatsapp_removido:
		"Los recordatorios por WhatsApp no se copian: configuralos de nuevo en este local.",
};

/** Notices (Spanish) for the `ajustes` the back applied to the copied config. Unknown codes are ignored. */
export const avisosDeAjustes = (ajustes) =>
	(Array.isArray(ajustes) ? ajustes : [])
		.map((a) => AVISOS_AJUSTES[a])
		.filter(Boolean);

const str = (v) => (v == null ? "" : String(v));

// Keeps only what the source defines, so the flow's own defaults stay for the rest.
const definidos = (obj) =>
	Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * Days and hours of the guided flows, which have ONE open/close pair for every open day. The pair of
 * the first open day is used; `unificado` tells that other days had different hours.
 */
export const horarioDe = (openingHours) => {
	const lista = Array.isArray(openingHours) ? openingHours : [];
	const abiertos = DIAS_SEMANA.map(({ weekday }) =>
		lista.find((h) => Number(h.weekday) === weekday),
	).filter((h) => h && h.enabled);
	if (!abiertos.length) return { unificado: false };
	const [ref] = abiertos;
	return {
		dias: abiertos.map((h) => Number(h.weekday)),
		desde: ref.open,
		hasta: ref.close,
		unificado: abiertos.some((h) => h.open !== ref.open || h.close !== ref.close),
	};
};

/** Fields every guided flow shares (location, hours, contact, booking policies). */
export const datosComunes = (local = {}) => {
	const { unificado, ...horario } = horarioDe(local.openingHours);
	const tel = local.contactPhone ? separarTelefono(local.contactPhone) : {};
	return definidos({
		nombre: local.name,
		stateid: local.stateid == null ? undefined : str(local.stateid),
		cityid: local.cityid == null ? undefined : str(local.cityid),
		address: local.address,
		lat: local.lat ?? undefined,
		lng: local.lng ?? undefined,
		...horario,
		descripcion: local.description,
		...tel,
		email: local.contactEmail,
		web: local.website,
		instagram: local.instagram,
		ventana: local.reservationWindowDays == null ? undefined : Number(local.reservationWindowDays),
		antelacion: local.minAdvanceMinutes == null ? undefined : Number(local.minAdvanceMinutes),
		tolerancia: local.toleranceMinutes == null ? undefined : Number(local.toleranceMinutes),
		cancela: local.cancellationAllowed == null ? undefined : local.cancellationAllowed !== false,
		plazo: local.cancellationPolicyHours == null ? undefined : Number(local.cancellationPolicyHours),
		notas: local.additionalNotes,
		__unificado: unificado,
	});
};

// What the flows keep aside to send at create time or in the steps after it; `avisos` feed the notice.
const importado = (config, extra, unificado) => {
	const { local = {}, ajustes } = config;
	const avisos = avisosDeAjustes(ajustes);
	if (unificado) {
		avisos.push("Los horarios eran distintos según el día: se usó el del primer día abierto. Revisalos.");
	}
	return {
		origen: local.name || "",
		avisos,
		shortDescription: local.shortDescription || "",
		amenities: Array.isArray(local.amenities) ? local.amenities : [],
		reminders: local.reminders || null,
		...extra,
	};
};

const sinMarca = ({ __unificado, ...datos }) => [datos, Boolean(__unificado)];

/* ---- club ---- */

const aTexto = (v) => (v == null || v === "" ? "" : String(v));

/** A court of the guided flow (prices by duration, details and deposit) from an imported table. */
export const canchaDeTabla = (t = {}) => {
	const filas = t.pricing?.defaultByDuration || [];
	const sena = filas.find((r) => Number(r.deposit) > 0)?.deposit;
	return {
		nombre: str(t.name).trim(),
		deporte: t.sport || "",
		superficie: t.surface || "",
		precios: Object.fromEntries(filas.map((r) => [Number(r.durationMinutes), aTexto(r.price)])),
		techado: t.enclosure || "",
		categoria: t.courtCategory || "",
		luz: Boolean(t.lighting),
		requiereSena: sena != null,
		sena: sena != null ? String(sena) : "",
	};
};

/** Pricing rules (day/time prices) of the imported tables, in the shape of the "reglas" step. */
export const reglasDeTablas = (tables = []) =>
	tables.flatMap((t) =>
		(t.pricing?.rules || []).map((r) => ({
			cancha: str(t.name).trim(),
			dur: Number(r.durationMinutes),
			dia: r.weekday == null ? "" : String(r.weekday),
			desde: r.fromTime || "",
			hasta: r.toTime || "",
			precio: aTexto(r.price),
			sena: Number(r.deposit) > 0 ? String(r.deposit) : "",
		})),
	);

export const datosImportadosDeClub = (config = {}) => {
	const { local = {}, tables = [] } = config;
	const [comunes, unificado] = sinMarca(datosComunes(local));
	return definidos({
		...comunes,
		deportes: Array.isArray(local.sports) ? local.sports : [],
		duraciones:
			Array.isArray(local.allowedDurations) && local.allowedDurations.length
				? local.allowedDurations.map(Number)
				: undefined,
		servicios: Array.isArray(local.services) ? local.services : [],
		categorias: Array.isArray(local.courtCategories) ? local.courtCategories.join(", ") : "",
		...(tables.length ? { canchas: tables.map(canchaDeTabla), reglas: reglasDeTablas(tables) } : {}),
		importado: importado(
			config,
			{
				gridGranularityMinutes: Number(local.gridGranularityMinutes) || 30,
				birthdays: local.birthdays || null,
			},
			unificado,
		),
	});
};

/* ---- bar ---- */

export const datosImportadosDeBar = (config = {}) => {
	const { local = {} } = config;
	const [comunes, unificado] = sinMarca(datosComunes(local));
	const zonas = Array.isArray(local.zones) ? local.zones : [];
	const conSena = local.reservationMode === "deposit";
	return definidos({
		...comunes,
		modo: conSena ? "sena" : "libre",
		sena: conSena ? str(local.depositAmount) : "",
		turno: local.slotDurationMinutes == null ? undefined : Number(local.slotDurationMinutes),
		maxPersonas: local.maxPartySize == null ? undefined : str(local.maxPartySize),
		zonePricing: Boolean(local.zonePricingEnabled),
		zonas: zonas.length
			? zonas.map((z) => ({
					nombre: str(z.name),
					mesas: str(z.tableCount),
					cap: str(z.tableCapacity),
					sena: z.priceEnabled && Number(z.price) > 0 ? str(z.price) : "",
				}))
			: undefined,
		importado: importado(config, {}, unificado),
	});
};

/* ---- salón ---- */

/** A service of the salon step; description, buffer, variants and add-ons travel as `extra`. */
export const servicioDeImportado = (s = {}) => {
	const extra = definidos({
		description: s.description || undefined,
		bufferAfterMinutes: Number(s.bufferAfterMinutes) > 0 ? Number(s.bufferAfterMinutes) : undefined,
		variants: Array.isArray(s.variants) && s.variants.length ? s.variants : undefined,
		addOns: Array.isArray(s.addOns) && s.addOns.length ? s.addOns : undefined,
	});
	return {
		...nuevoServicio(),
		nombre: str(s.name),
		categoria: str(s.category),
		duracion: str(s.durationMinutes),
		precio: aTexto(s.price),
		sena: Number(s.deposit) > 0 ? String(s.deposit) : "",
		extra,
	};
};

export const datosImportadosDeSalon = (config = {}) => {
	const { local = {}, services = [] } = config;
	const [comunes, unificado] = sinMarca(datosComunes(local));
	return {
		...comunes,
		...(services.length ? { servicios: services.map(servicioDeImportado) } : {}),
		importado: importado(
			config,
			{
				rescheduling: local.rescheduling || null,
				clientFields: Array.isArray(local.clientFields) ? local.clientFields : [],
			},
			unificado,
		),
	};
};

const POR_TIPO = {
	club: datosImportadosDeClub,
	bar: datosImportadosDeBar,
	beauty: datosImportadosDeSalon,
};

/** Guided-flow state patch for an imported config (`config.tipo` decides the module). */
export const datosImportados = (config) => {
	const mapear = POR_TIPO[config?.tipo];
	return mapear ? mapear(config) : {};
};

/* ---- club birthdays (after the courts exist) ---- */

const norm = (n) => str(n).trim().toLowerCase();

/**
 * Birthday config for the new club: `courtNames` become the ids of the new courts. Returns
 * `{ payload, avisos }`; `payload` is null when there is nothing (valid) to save.
 */
export const cumpleanosParaClubNuevo = (birthdays, canchas = []) => {
	if (!birthdays || !(birthdays.enabled || (birthdays.packages || []).length)) {
		return { payload: null, avisos: [] };
	}
	const porNombre = new Map(canchas.map((c) => [norm(c.name), String(c._id || c.id)]));
	const nombres = Array.isArray(birthdays.courtNames) ? birthdays.courtNames : [];
	const courtIds = nombres.map((n) => porNombre.get(norm(n))).filter(Boolean);
	const avisos = [];
	let enabled = birthdays.enabled === true;
	if (enabled && !courtIds.length) {
		enabled = false;
		avisos.push("Los cumpleaños se copiaron sin habilitar: ninguna de sus canchas existe en este club.");
	} else if (courtIds.length < nombres.length) {
		avisos.push("Algunas canchas de cumpleaños no se pudieron asociar a este club.");
	}
	const { courtNames, ...resto } = birthdays;
	const form = normalizeBirthdays({ ...resto, enabled, courtIds });
	if (hasBirthdayErrors(validateBirthdays(form))) {
		return {
			payload: null,
			avisos: [...avisos, "No se copió la configuración de cumpleaños: tiene datos que hay que revisar. Cargala desde la pestaña Cumpleaños."],
		};
	}
	return { payload: buildBirthdaysPayload(form), avisos };
};
